import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import DataTable, { Column, ExpandedColumn } from '@repo/ui/data-table'
import { TableDate } from '@repo/ui/data-table-cells'
import { EuroIcon } from '@repo/ui/icons'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { API } from '../../api/apis.config'
import { getPcrPayments } from '../../api/pcr/get-pcr-payments'
import DynamicDataTable from '../../components/elements/dynamic-table'
import UserNavBar from '../../components/elements/nav-bar'
import { Paths } from '../../lib/constants/path'
import useAuth from '../../lib/hooks/use-auth'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import { PcrPaymentTableData } from '../../lib/types/models/pcr/pcr'
import {
  PcrStaffApplicationFilters,
  StaffApplication
} from '../../lib/types/models/staff-application/staff-application'
import { capitalize, yearsFilterOptions } from './utils'

const PcrPaymentsPage = () => {
  const { user } = useAuth()
  const { navigateWithEvent } = useNavigator()
  const [year, setYear] = useState(new Date().getFullYear())

  const { data } = useQuery({
    queryKey: ['pcr-payments', user?.pcr?.id, year],
    queryFn: () => {
      if (user?.pcr?.id) return getPcrPayments(user.pcr.id, { year })
    },
    enabled: !!user?.pcr?.id
  })

  const paymentColumns: Column<PcrPaymentTableData>[] = [
    {
      accessorKey: 'month',
      header: 'Mois',
      cell: (data) => capitalize(data.row.original.month?.toString())
    },
    {
      accessorKey: 'year',
      header: 'Année',
      cell: (data) => <Badge text={data.row.original.year?.toString()} variant="grey" />
    },
    {
      accessorKey: 'amount',
      header: 'Montant',
      cell: (data) => <Badge text={`€${data.row.original.amount.toFixed(2)}`} variant="primary" />
    }
  ]

  const columns: Column<StaffApplication>[] = [
    {
      accessorKey: 'id',
      header: '# Candidature',
      enableSorting: true
    },
    {
      accessorKey: 'firstName',
      header: 'NOM du candidat',
      enableSorting: true,
      cell: (data) =>
        `${data.row.original.lastName.toUpperCase()} ${StringFormatter.capitalize(data.row.original.firstName)}`
    },
    {
      accessorKey: 'dateAssigned',
      header: 'Date du paiement',
      enableSorting: true,
      cell: (data) =>
        data.row.original.pcrScoreCard?.dateOfPayment ? (
          <TableDate date={data.row.original.pcrScoreCard?.dateOfPayment} />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'pcrScoreCard.amountPaidToPCR',
      header: 'Montant',
      enableSorting: true,
      cell: (data) =>
        data.row.original.pcrScoreCard?.amountPaidToPCR
          ? `${data.row.original.pcrScoreCard.amountPaidToPCR} €`
          : '-'
    }
  ]

  const getAmountByMonth = (month: number) => {
    const monthData = data?.filter((d) => new Date(d.date).getMonth() === month - 1)
    return monthData?.reduce((acc, curr) => acc + curr.amount, 0) ?? 0
  }

  const similarApplicationColumns: ExpandedColumn<StaffApplication, StaffApplication>[] = [
    {
      accessorKey: 'id',
      display: (a) => a.id
    },
    {
      accessorKey: 'firstName',
      display: (a) => `${StringFormatter.capitalize(a.firstName)} ${a.lastName.toUpperCase()}`
    },
    {
      accessorKey: 'dateAssigned',
      display: (a) =>
        a.pcrScoreCard?.dateOfPayment ? <TableDate date={a.pcrScoreCard?.dateOfPayment} /> : '-'
    },
    {
      accessorKey: 'pcrScoreCard.amountPaidToPCR',
      display: (a) =>
        a.pcrScoreCard?.amountPaidToPCR ? `${a.pcrScoreCard.amountPaidToPCR} €` : '-'
    }
  ]

  if (!user?.pcr?.id) return null

  return (
    <div>
      <UserNavBar title={'Récapitulatif des candidatures payées'} icon={EuroIcon} />
      <div className="h-4" />

      <div className="flex justify-between gap-3 items-start">
        <DynamicDataTable<StaffApplication, PcrStaffApplicationFilters, StaffApplication>
          title={'Récapitulatif des candidatures payées'}
          deleteMessage={{
            name: 'candidature',
            attributeName: 'firstName',
            deleteOne: (staffApplication) =>
              `Êtes-vous sûr de vouloir supprimer la candidature de "${StringFormatter.capitalize(staffApplication.firstName)} ${staffApplication.lastName.toUpperCase()}"?`,
            deleteMany: (staffApplications) =>
              `Êtes-vous sûr de vouloir supprimer ces candidatures "${staffApplications
                .map(
                  (staffApplication) =>
                    `${StringFormatter.capitalize(staffApplication.firstName)} ${staffApplication.lastName.toUpperCase()}`
                )
                .join('", "')}"?`
          }}
          columns={columns}
          className="w-4/6"
          allowSearchParam
          permissions={{ delete: false }}
          filters={{
            showWithdrawApplications: true,
            isPaid: true
          }}
          expandedOptions={{ key: 'similarApplications', columns: similarApplicationColumns }}
          endpoint={API.pcr.getStaffApplications.url.replace(':id', user.pcr.id.toString())}
          onRowClick={
            user.role !== RoleEnum.ADMIN
              ? undefined
              : (row, event) => {
                  navigateWithEvent(
                    Paths.UPDATE_STAFF_APPLICATION_PAGE.replace(':id', row.id.toString()),
                    event
                  )
                }
          }
        />

        <Box className="flex-1">
          <div className="flex justify-between items-center py-2 mb-8">
            <div className="flex items-center justify-between gap-4 w-full">
              <Typography variant="sub-heading">Liste des paiements</Typography>
              <SelectInput<number>
                options={yearsFilterOptions}
                selectedOption={{
                  label: yearsFilterOptions.find((y) => y.value === year)?.label ?? '',
                  value: year
                }}
                onChange={setYear}
                label="Année"
              />
            </div>
          </div>
          <DataTable<PcrPaymentTableData>
            columns={paymentColumns}
            data={Array.from({ length: 12 }, (_, i) => ({
              id: i,
              month: new Date(year, i).toLocaleString('fr', { month: 'long' }),
              year,
              amount: getAmountByMonth(i + 1)
            }))}
            rowCount={50}
            paginated={false}
          />
        </Box>
      </div>
    </div>
  )
}

export default PcrPaymentsPage
